import { StandardEvents } from '@shopify/events';

(() => {
  'use strict';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = navigator.connection?.saveData === true;
  const noMotion = () => reduce.matches || saveData;
  const init = (root = document) => {
    root.querySelectorAll('[data-motion-scene]:not([data-tm-ready])').forEach(scene => {
      scene.dataset.tmReady = 'true';
      const button = scene.querySelector('[data-motion-toggle]');
      const video = scene.querySelector('video');
      let paused = noMotion();
      let visible = true;
      const sync = () => {
        const stopped = paused || noMotion() || !visible || document.hidden;
        scene.classList.toggle('tm-motion-paused', stopped);
        scene.classList.toggle('tm-no-motion', noMotion());
        if (button) {
          button.setAttribute('aria-label', paused ? 'Play background motion' : 'Pause background motion');
          button.title = button.getAttribute('aria-label');
          button.firstElementChild.textContent = paused ? '\u25b6' : '\u275a\u275a';
        }
        if (video) { if (stopped) video.pause(); else video.play().catch(() => {}); }
      };
      button?.addEventListener('click', () => { paused = !paused; sync(); });
      document.addEventListener('visibilitychange', sync);
      reduce.addEventListener('change', sync);
      if ('IntersectionObserver' in window) new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: 0.05 }).observe(scene);
      if (matchMedia('(pointer:fine)').matches) {
        let frame = 0;
        scene.addEventListener('pointermove', event => {
          if (noMotion() || paused) return;
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const rect = scene.getBoundingClientRect();
            scene.style.setProperty('--scene-x', `${((event.clientX - rect.left) / rect.width - .5) * 10}px`);
            scene.style.setProperty('--scene-y', `${((event.clientY - rect.top) / rect.height - .5) * 6}px`);
          });
        }, { passive: true });
        scene.addEventListener('pointerleave', () => { scene.style.setProperty('--scene-x','0px'); scene.style.setProperty('--scene-y','0px'); });
      }
      sync();
    });
    if (!noMotion() && 'IntersectionObserver' in window) {
      const reveal = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.animate([{ opacity:.3, transform:'translateY(18px)' }, { opacity:1, transform:'translateY(0)' }], { duration:650, easing:'cubic-bezier(.2,.7,.2,1)' });
        reveal.unobserve(entry.target);
      }), { threshold:.07 });
      root.querySelectorAll('[data-tm-reveal]:not([data-reveal-ready])').forEach(el => { el.dataset.revealReady='true'; reveal.observe(el); });
    }
    root.querySelectorAll('.tm-mobile-menu:not([data-menu-ready])').forEach(menu => {
      menu.dataset.menuReady = 'true';
      menu.addEventListener('keydown', event => { if (event.key === 'Escape') { menu.open=false; menu.querySelector('summary').focus(); } });
      menu.querySelectorAll('a').forEach(a => a.addEventListener('click', () => { menu.open=false; }));
      document.addEventListener('pointerdown', event => { if (!menu.contains(event.target)) menu.open=false; });
    });
    root.querySelectorAll('[data-magfold-modes]:not([data-modes-ready])').forEach(component => {
      component.dataset.modesReady = 'true';
      const buttons = [...component.querySelectorAll('[data-mode]')];
      const image = component.querySelector('[data-mode-image]');
      const index = component.querySelector('[data-mode-index]');
      const title = component.querySelector('[data-mode-title]');
      const text = component.querySelector('[data-mode-text]');
      const source = component.querySelector('[data-mode-content]');
      if (!buttons.length || !image || !source) return;
      let content;
      try { content = JSON.parse(source.textContent); } catch { return; }
      const select = (button, focus = false) => {
        const next = content[button.dataset.mode];
        if (!next) return;
        buttons.forEach(item => item.setAttribute('aria-selected', String(item === button)));
        image.classList.add('is-changing');
        const swap = () => {
          image.src = next.image;
          image.alt = next.alt;
          index.textContent = next.index;
          title.textContent = next.title;
          text.textContent = next.text;
          requestAnimationFrame(() => image.classList.remove('is-changing'));
        };
        window.setTimeout(swap, noMotion() ? 0 : 120);
        if (focus) button.focus();
      };
      buttons.forEach((button, position) => {
        button.addEventListener('click', () => select(button));
        button.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (position + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
          select(buttons[nextIndex], true);
        });
      });
    });
    root.querySelectorAll('[data-tech-tabs]:not([data-tech-ready])').forEach(component => {
      component.dataset.techReady = 'true';
      const buttons = [...component.querySelectorAll('[data-tech-tab]')];
      const image = component.querySelector('[data-tech-image]');
      const title = component.querySelector('[data-tech-title]');
      const copy = component.querySelector('[data-tech-text]');
      const source = component.querySelector('[data-tech-content]');
      if (!buttons.length || !image || !title || !copy || !source) return;
      let content;
      try { content = JSON.parse(source.textContent); } catch { return; }
      const select = (button, focus = false) => {
        const next = content[button.dataset.techTab];
        if (!next) return;
        buttons.forEach(item => item.setAttribute('aria-selected', String(item === button)));
        image.classList.add('is-changing');
        window.setTimeout(() => {
          image.src = next.image;
          image.alt = next.alt;
          title.textContent = next.title;
          copy.textContent = next.text;
          requestAnimationFrame(() => image.classList.remove('is-changing'));
        }, noMotion() ? 0 : 110);
        if (focus) button.focus();
      };
      buttons.forEach((button, position) => {
        button.addEventListener('click', () => select(button));
        button.addEventListener('keydown', event => {
          if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (position + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
          select(buttons[nextIndex], true);
        });
      });
    });
    const toc = root.querySelector('[data-article-toc]');
    const body = root.querySelector('[data-article-body]');
    if (toc && body && !toc.children.length) body.querySelectorAll('h2').forEach((heading, index) => {
      heading.id ||= `story-section-${index+1}`;
      const link = document.createElement('a'); link.href=`#${heading.id}`; link.textContent=heading.textContent; toc.append(link);
    });
  };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener(StandardEvents.cartLinesUpdate, event => {
    event.promise?.then(({ cart, detail }) => {
      const count = cart?.totalQuantity ?? detail?.itemCount;
      if (Number.isInteger(count)) document.querySelectorAll('[data-tm-cart-count]').forEach(el => { el.textContent=String(count); });
    }).catch(() => {});
  });
  init();
})();
